import { useApolloClient } from "@apollo/client";
import { faFileImport } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MenuItemWithIcon } from "@msys/ui";
import { CircularProgress, Icon } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import { useRef, useState } from "react";
import { GaebFileUploader } from "./GaebPFileUploader.js";
import { useCreateQuoteFromGaebImportMutation } from "./GaebPImport.generated.js";

type Props = {
  projectId: string;
  refetchQueries?: string[];
  type: "p94";
};

export const ImportGaebButton = ({
  projectId,
  refetchQueries,
  type,
}: Props) => {
  const client = useApolloClient();
  const { t } = useTranslate("Projects");
  const [createQuoteFromGaebImportMutation] =
    useCreateQuoteFromGaebImportMutation({
      client,
    });
  const { enqueueSnackbar } = useSnackbar();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");

  async function importGaeb(filename: string) {
    await createQuoteFromGaebImportMutation({
      variables: {
        input: {
          filename,
          projectId,
          gaebType: "p94",
        },
      },
      refetchQueries,
    });

    return undefined;
  }

  return (
    <MenuItemWithIcon
      icon={
        status !== "loading" ? (
          <Icon>
            <FontAwesomeIcon icon={faFileImport} />
          </Icon>
        ) : (
          <CircularProgress size={"1rem"} />
        )
      }
      onClick={async () => {
        fileInputRef.current!.click();
      }}
      disabled={status === "loading"}
    >
      <GaebFileUploader
        innerRef={fileInputRef}
        onStart={() => setStatus("loading")}
        onCancel={() => setStatus("idle")}
        onError={e => {
          if (e instanceof Error)
            enqueueSnackbar(e.message, { variant: "error" });
          Sentry.captureException(e);
          setStatus("error");
        }}
        onUploadFile={async file => {
          setStatus("loading");
          try {
            await importGaeb(file.name);
            setStatus("idle");
          } catch (e) {
            if (e instanceof Error)
              enqueueSnackbar(e.message, { variant: "error" });
            Sentry.captureException(e);
            setStatus("error");
          }
        }}
        type={type}
      />
      {`${t("Import")} GAEB ${type}`}
    </MenuItemWithIcon>
  );
};
